import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, Observable, of } from "rxjs";
import { delay, map, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import type { PricingBillingPeriod } from "../../shared/services/pricing.service";

const STORAGE_KEY = "rayalina_current_auth";

export interface RayalinaAuthPayload {
    accessToken?: string;
    contact?: {
        email?: string;
        name?: string;
        [key: string]: unknown;
    };
    [key: string]: unknown;
}

interface RayalinaRegisterBody {
    name: string;
    email: string;
    phonePrimary: string;
    password: string;
    passwordConfirm: string;
}

@Injectable({
    providedIn: "root",
})
export class AuthService {
    private readonly authSubject = new BehaviorSubject<RayalinaAuthPayload | null>(this.readStored());

    readonly currentAuth$: Observable<RayalinaAuthPayload | null> = this.authSubject.asObservable();

    constructor(
        private readonly http: HttpClient,
        private readonly router: Router,
    ) {}

    get currentAuth(): RayalinaAuthPayload | null {
        return this.authSubject.value;
    }

    get isLoggedIn(): boolean {
        return !!this.currentAuth?.accessToken;
    }

    buildCheckoutUrl(planId: string, billing: PricingBillingPeriod = "yearly"): string {
        const tree = this.router.createUrlTree(["/checkout"], {
            queryParams: { plan: planId, billing },
        });
        return this.router.serializeUrl(tree);
    }

    saveAuth(auth: RayalinaAuthPayload): void {
        if (auth?.accessToken) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
            this.authSubject.next(auth);
        }
    }

    logout(): void {
        localStorage.removeItem(STORAGE_KEY);
        this.authSubject.next(null);
    }

    login(email: string, password: string): Observable<RayalinaAuthPayload> {
        if (environment.useMockAuth) {
            return this.mockLogin$(email, password);
        }
        const url = `${environment.apiUrl}/auth/login`;
        return this.http.post<RayalinaAuthPayload>(url, { email, password }).pipe(
            tap((res) => this.saveAuth(res)),
        );
    }

    register(body: RayalinaRegisterBody): Observable<RayalinaAuthPayload> {
        if (environment.useMockAuth) {
            return this.mockRegister$(body);
        }
        const url = `${environment.apiUrl}/auth/register`;
        return this.http.post<RayalinaAuthPayload>(url, body).pipe(
            map((res) => {
                if (res?.accessToken) {
                    this.saveAuth(res);
                }
                return res;
            }),
        );
    }

    navigateAfterAuth(returnUrl: string | null | undefined): void {
        const target = returnUrl?.trim() || this.buildCheckoutUrl("trial");
        this.router.navigateByUrl(target);
    }

    private readStored(): RayalinaAuthPayload | null {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) {
                return null;
            }
            return JSON.parse(raw) as RayalinaAuthPayload;
        } catch {
            return null;
        }
    }

    private mockLogin$(email: string, _password: string): Observable<RayalinaAuthPayload> {
        const payload = this.buildMockAuth({
            email,
            name: this.displayNameFromEmail(email),
        });
        return of(payload).pipe(
            delay(400),
            tap((p) => this.saveAuth(p)),
        );
    }

    private mockRegister$(body: RayalinaRegisterBody): Observable<RayalinaAuthPayload> {
        const payload = this.buildMockAuth({
            email: body.email,
            name: body.name,
            phonePrimary: body.phonePrimary,
        });
        return of(payload).pipe(
            delay(500),
            tap((p) => this.saveAuth(p)),
        );
    }

    private buildMockAuth(contact: {
        email: string;
        name: string;
        phonePrimary?: string;
    }): RayalinaAuthPayload {
        return {
            accessToken: `mock_${Date.now()}_${Math.random().toString(36).slice(2, 12)}`,
            contact: {
                email: contact.email,
                name: contact.name,
                ...(contact.phonePrimary ? { phonePrimary: contact.phonePrimary } : {}),
            },
        };
    }

    private displayNameFromEmail(email: string): string {
        const local = email.split("@")[0]?.trim();
        if (!local) {
            return "Utilisateur";
        }
        return local.replace(/[._-]+/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
    }
}
